import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { SpanStatusCode, context, trace } from '@opentelemetry/api';
import { inject } from '@angular/core';
import { TracingService } from '@app/services/tracing/tracing.service';
import { W3CTraceContextPropagator } from '@opentelemetry/core';
import { environment } from '@environment/environment';

@Injectable()
export class TracingInterceptor implements HttpInterceptor {
  private readonly tracingService = inject(TracingService);
  private readonly propagator = new W3CTraceContextPropagator();

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (environment.CORALOGIX_RUM_API_KEY) {
      return next.handle(req);
    }
    // Local execution for Jagger
    const urlPath = req.url.split('?')[0];
    const urlHost = window.location.host;

    const span = this.tracingService.startSpan(`HTTP Request - ${req.method} - ${req.url}`, {
      'http.url': req.url,
      'http.method': req.method,
      'http.target': urlPath,
      'http.host': urlHost,
    });

    const ctx = trace.setSpan(context.active(), span);
    const headers: Record<string, string> = {};
    this.propagator.inject(ctx, headers, {
      set: (carrier, key, value) => (carrier[key] = value),
    });

    const tracedReq = req.clone({
      setHeaders: {
        ...headers,
      },
    });

    return next.handle(tracedReq).pipe(
      tap({
        next: (event) => {
          if (event instanceof HttpResponse) {
            span.setAttributes({
              'http.url': req.url,
              'http.method': req.method,
              'http.status_code': event.status.toString(),
            });
            span.end();
          }
        },
        error: (error: HttpErrorResponse) => {
          span.setStatus({
            code: SpanStatusCode.ERROR,
            message: error.message,
          });
          span.setAttributes({
            'http.url': req.url,
            'http.method': req.method,
            'http.status_code': error.status.toString(),
            'error.type': error.name,
            'error.message': error.message,
          });
          span.end();
        },
      }),
    );
  }
}
